import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCompany } from './queryKeys';

/**
 * Deletes a company logo.
 *
 * @param {string} companyId - The ID of the company for which to delete the logo.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/remove_company_logo_company__id__logo_delete
 */
export const deleteCompanyLogo = async (companyId: UUID) => {
  const response = await vestigasApi
    .delete(ENDPOINT.COMPANY.DELETE_LOGO(companyId))
    .json();

  return response;
};

/**
 * React Query mutation hook for deleting a company logo.
 *
 * This hook provides functionality to:
 * - Delete the logo image for a company
 * - Automatically invalidate the company query cache on completion
 *
 * @returns {UseMutationResult} A mutation object containing the delete function and status
 *
 * @example
 * const mutation = useMutationCompanyLogoDelete(companyId);
 * mutation.mutate();
 */
export const useMutationCompanyLogoDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: UUID) => deleteCompanyLogo(companyId),
    onSettled(_, __, companyId) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.get(companyId),
      });
    },
    onSuccess() {
      // ... show toast or something
    },
  });
};
